import type { ActionPhare, EchecPlan, PlanActions as Plan } from "../lib/api";
import { useApp } from "../state/AppState";
import { Rassurance } from "./Rassurance";
import { BoutonAchat } from "./BoutonAchat";

/**
 * Le plan en dix actions : **la première offerte en entier**, les neuf autres
 * avec leur vrai titre et leur contenu seul verrouillé.
 *
 * Ce que ce bloc remplace : deux actions courtes et huit cartes floutées
 * portant des intitulés de rubrique identiques pour tout le monde. Le teaser
 * ressemblait à un sommaire, et l'acheteur n'avait aucun moyen d'estimer ce
 * qu'il achetait.
 *
 * Le renversement tient en deux gestes. **On donne tout sur une action** — pas
 * un aperçu, le travail complet, applicable tel quel — ce qui prouve le niveau
 * des neuf suivantes au lieu de le promettre. Et **on montre les vrais titres**
 * des neuf autres au lieu de les flouter : la curiosité naît de ce qu'on voit
 * précisément manquer, pas d'une tache grise. C'est la seule forme de rareté
 * qui soit vérifiable par celui à qui on la présente.
 */

export function PlanActions({ plan, chargement }: { plan: Plan | null; chargement: boolean }) {
  const { reportPrice } = useApp();

  if (chargement) return <PlanEnAttente />;
  if (plan === null) return null;

  return (
    <div className="block">
      <h2>Votre plan en dix actions</h2>
      <p className="sub">
        Voici la première, avec sa raison d'être, ses étapes et le piège à éviter. Le rapport
        l'achève et détaille les neuf autres.
      </p>

      <ActionOfferte action={plan.action} />

      <TitresVerrouilles titres={plan.verrouillees} />

      <div className="verrous-pied">
        <p>
          Ces neuf titres sont les vôtres — ils portent sur les tâches que vous venez de déclarer.
          Chacune est écrite au format de l'action 01, complète : le pourquoi, chaque étape
          détaillée, le prompt à reprendre, le piège et la vérification.
        </p>
        {/*
          Second rang, et c'est un arbitrage assumé.

          Deux boutons pleine largeur se suivaient ici : celui-ci en laiton
          plein, puis « Volet suivant » en bloc bleu nuit plein — plus lourd
          encore. Deux actions de même poids ne font aucune hiérarchie, elles
          font hésiter. Le volet gratuit passe donc au premier rang, parce que
          c'est lui qui mène à l'attestation, et que l'attestation est ce qui se
          partage : la croissance du produit ne tient qu'au partage.

          On vend aussi mieux après : le tunnel le dit lui-même — sans le volet
          3, les dix actions sont écrites sans savoir si l'on débute ou si l'on
          maîtrise déjà.
        */}
        <BoutonAchat
          plan="report"
          className="btn btn-contour"
          libelle={`Débloquer les neuf autres — ${reportPrice.toLocaleString("fr-FR")} F`}
        />
        <p className="fine" style={{ textAlign: "center", marginTop: "10px" }}>
          Remboursé sur simple demande, sans justification, dans les quatorze jours.
        </p>
        <Rassurance />
      </div>
    </div>
  );
}

/**
 * Le cadenas, dessiné plutôt qu'importé : deux formes, aucune dépendance
 * ajoutée. `aria-hidden` parce que le libellé est porté par le conteneur —
 * sans quoi la ligne serait annoncée deux fois.
 */
function Cadenas() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        d="M4.5 7V4.75a3.5 3.5 0 0 1 7 0V7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <rect x="2.6" y="7" width="10.8" height="7.4" rx="1.4" fill="currentColor" />
    </svg>
  );
}

/**
 * La pile des neuf titres verrouillés — partagée entre les deux axes.
 *
 * Un cadenas dessiné, pas « ⌁ » ni le mot en toutes lettres : le glyphe ne
 * disait rien et était masqué aux lecteurs d'écran ; le mot écrit prenait un
 * quart de la ligne, neuf fois, et repliait les titres sur cinq lignes — alors
 * que ce sont eux qu'on lit pour décider de payer. Le dessin coûte trente
 * pixels, et `aria-label` dit le mot à qui ne le voit pas.
 */
export function TitresVerrouilles({ titres }: { titres: string[] }) {
  return (
    <div className="verrous">
      {titres.map((titre, i) => (
        <div className="verrou" key={titre}>
          <div className="n">{String(i + 2).padStart(2, "0")}</div>
          <div className="t">{titre}</div>
          <div className="cadenas" role="img" aria-label="Verrouillé">
            <Cadenas />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ActionOfferte({ action }: { action: ActionPhare }) {
  return (
    <div className="apercu">
      <div className="apercu-head">
        <span>Action 01 · le début vous est offert</span>
        <b>
          {action.effort} · environ {action.gainHeures} h par semaine
        </b>
      </div>

      <h3>{action.titre}</h3>

      <div className="apercu-part">
        <div className="k">Pourquoi celle-ci d'abord</div>
        <p>{action.pourquoi}</p>
      </div>

      {/*
        Toutes les étapes portent leur titre : c'est l'ampleur du travail qui
        doit se voir. Seule la première est rédigée — elle prouve la qualité
        d'écriture des suivantes au lieu de la promettre. Le détail des autres
        n'est pas masqué, il n'est simplement pas envoyé par le serveur.
      */}
      <div className="apercu-part">
        <div className="k">Les étapes, dans l'ordre</div>
        <ol className="apercu-etapes">
          {action.etapes.map((etape) => (
            <li key={etape.titre}>
              <b>{etape.titre}</b>
              {etape.detail !== undefined ? (
                <p className="detail">{etape.detail}</p>
              ) : (
                <span className="scelle">Détaillée dans le rapport</span>
              )}
            </li>
          ))}
        </ol>
      </div>

      {/*
        Le piège reste offert, et c'est délibéré : c'est le seul bloc qui
        protège au lieu d'habiliter. Le donner ne coûte rien et dit la bonne foi.
      */}
      <div className="apercu-part">
        <div className="k">Le piège à éviter</div>
        <p>{action.piege}</p>
      </div>

      <div className="apercu-part apercu-retenu">
        <div className="k">Ce que contient la suite</div>
        <ul>
          <li>Le texte à copier-coller dans l'assistant — ce qu'on appelle un « prompt » — écrit pour cette tâche</li>
          <li>
            Le détail des {action.retenu.etapes} étapes suivantes
          </li>
          <li>Le critère de vérification — à quoi vous voyez que c'est juste</li>
        </ul>
      </div>
    </div>
  );
}

/**
 * L'attente est annoncée pour ce qu'elle est. Le score est déjà affiché
 * au-dessus : personne n'attend devant un écran vide, et la rédaction d'une
 * demi-page prend le temps qu'elle prend.
 */
function PlanEnAttente() {
  return (
    <div className="block">
      <h2>Votre plan en dix actions</h2>
      <p className="sub" aria-live="polite">
        Nous rédigeons votre première action. Une trentaine de secondes — votre indice reste
        affiché au-dessus pendant ce temps.
      </p>
      <div className="attente-plan" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((i) => (
          <i key={i} style={{ width: `${[92, 78, 88, 61, 70][i]}%` }} />
        ))}
      </div>
    </div>
  );
}

/** Ce qu'on dit selon la cause. Jamais « une erreur est survenue ». */
const EXPLICATION: Record<EchecPlan, string> = {
  trop_de_demandes:
    "Trop de demandes depuis cette connexion sur la dernière heure. Réessayez dans quelques minutes : votre indice, lui, reste affiché.",
  budget:
    "Notre plafond de dépense du jour est atteint. C'est une limite que nous nous imposons pour ne pas laisser filer les coûts ; elle se lève demain.",
  modele:
    "La rédaction n'a pas abouti cette fois. Nous préférons ne rien afficher plutôt qu'une action générique qui ne vaudrait pas la vôtre.",
  reseau: "La connexion s'est interrompue avant que la réponse n'arrive.",
  inattendu: "Nous n'avons pas pu rédiger votre première action.",
};

/**
 * L'échec est dit, et rattrapable.
 *
 * Il ne l'était pas : un plan absent faisait retomber l'écran sur l'affichage
 * précédent, sans un mot. La personne voyait une version dégradée du produit
 * en croyant voir le produit, et rien ne lui permettait de réessayer.
 */
export function PlanEchoue({
  raison,
  onReessayer,
}: {
  raison: EchecPlan;
  onReessayer: () => void;
}) {
  const { reportPrice } = useApp();
  return (
    <div className="block">
      <h2>Votre plan en dix actions</h2>
      <div className="notice" role="status">
        <b>Votre première action n'a pas pu être rédigée.</b> {EXPLICATION[raison]}
      </div>
      <button type="button" className="btn btn-ghost" onClick={onReessayer}>
        Réessayer
      </button>

      {/*
        L'encadré d'échec emportait tout le bloc : la personne perdait l'action
        offerte **et** toute mention du rapport. L'écran ne proposait plus rien
        à acheter. On ne peut pas montrer un plan qu'on n'a pas ; on peut
        continuer de dire ce que contient le rapport.
      */}
      <div className="verrous-pied" style={{ marginTop: "26px" }}>
        <p>
          Le rapport, lui, ne dépend pas de cette rédaction : il contient vos dix actions
          complètes — le pourquoi, chaque étape détaillée, le prompt à reprendre, le piège et la
          vérification.
        </p>
        <BoutonAchat
          plan="report"
          libelle={`Obtenir mon rapport — ${reportPrice.toLocaleString("fr-FR")} F`}
        />
        <p className="fine" style={{ textAlign: "center", marginTop: "10px" }}>
          Remboursé sur simple demande, sans justification, dans les quatorze jours.
        </p>
        <Rassurance />
      </div>
    </div>
  );
}
